import axios from "axios";

/**
 * DischargeSummaryService
 *
 * Service for discharge-summary-related API calls.
 * Follows the same pattern as GenerateAbhaService.
 *
 * All calls go to the main backend:
 *   - MANAGE_DEV_BASE → REACT_APP_BACKEND_ROUTE_URL — template list/select, generate,
 *       validate, save, get, upload-pdf
 */

// Main backend base — same env var as the rest of the discharge flow
// (see pages/ipd/api/dischargeProcessApi.js).
const MANAGE_DEV_BASE = (
  process.env.REACT_APP_BACKEND_ROUTE_URL || ""
).replace(/\/$/, "");

class DischargeSummaryService {
  // ─── API 2 — List available templates ────────────────────────────────
  /**
   * Fetch all available discharge templates for the org.
   * Uses only ORG-CODE and APP-MODE headers.
   *
   * POST /api/list_discharge_templates/
   *
   * @param {object} headers - Must contain at least { "ORG-CODE", "APP-MODE" }.
   * @returns {Promise<object>} { templates: [...] }
   */
  async listTemplates(headers) {
    const endpoint = `${MANAGE_DEV_BASE}/list_discharge_templates/`;

    const response = await axios.post(endpoint, JSON.stringify({}), {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });

    return response.data;
  }

  // ─── API 2 (variant) — Bind template to admission ───────────────────
  /**
   * Select/bind a template to a specific admission.
   *
   * POST /api/list_discharge_templates/
   *
   * @param {number|string} admissionId - The admission to bind.
   * @param {number|string} templateId  - The template to select.
   * @param {object} headers - Must contain at least { "ORG-CODE", "APP-MODE" }.
   * @returns {Promise<object>} Server response.
   */
  async selectTemplate(admissionId, templateId, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/list_discharge_templates/`;

    const response = await axios.post(
      endpoint,
      JSON.stringify({
        admission_id: admissionId,
        template_id: templateId,
      }),
      {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }
    );

    return response.data;
  }

  // ─── API 3 — Generate discharge summary ──────────────────────────────
  /**
   * Generate discharge summary JSON from care plan / admission data.
   *
   * POST /api/generate_discharge_summary/
   *
   * @param {number|string} admissionId - Admission ID.
   * @param {object} headers - Full auth headers.
   * @returns {Promise<object>} The generated summary JSON.
   */
  async generateSummary(admissionId, templateId, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/generate_discharge_summary/`;

    const body = {
      admission_id: parseInt(admissionId, 10),
      template_id: parseInt(templateId, 10)
    };

    const response = await axios.post(
      endpoint,
      JSON.stringify(body),
      {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }
    );

    return response.data;
  }

  // ─── API 4 — Validate discharge payload ──────────────────────────────
  /**
   * Validate and auto-fix a discharge summary payload via the backend.
   *
   * POST /api/validate_discharge_payload/
   *
   * @param {object} payload - The discharge summary JSON to validate.
   * @param {object} headers - Auth headers (Authorization, Content-Type, etc.).
   * @returns {Promise<object>} The server-validated/fixed payload.
   * @throws {Error} On network or server error.
   */
  async validatePayload(payload, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/validate_discharge_payload/`;

    // Build the request body: admission_id at top level, clean payload without server-only fields
    const { admission_id, pdf_url, ...cleanPayload } = payload;

    const body = { payload: cleanPayload };
    if (admission_id !== undefined && admission_id !== null) {
      body.admission_id = admission_id;
    }

    const response = await axios.post(endpoint, JSON.stringify(body), {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });

    return response.data;
  }

  // ─── API 5 — Save / persist discharge summary ───────────────────────
  /**
   * Save a discharge summary (draft or final) to the backend.
   *
   * POST /api/save_discharge_summary/
   *
   * @param {object} payload   - The full discharge summary JSON.
   * @param {object} headers   - Full auth headers.
   * @returns {Promise<object>} Server response (may include draft id).
   */
  async saveDischargeSummary(payload, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/save_discharge_summary/`;

    const response = await axios.post(endpoint, JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });

    return response.data;
  }

  // ─── API 5.5 — Get existing discharge summary ───────────────────────────────────
  /**
   * Check if a discharge summary exists for this admission.
   *
   * POST /api/get_discharge_summary/
   *
   * @param {number|string} admissionId - Admission ID.
   * @param {object} headers - Full auth headers.
   * @returns {Promise<object>} Server response with { found, final_json }.
   */
  async getDischargeSummary(admissionId, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/get_discharge_summary/`;

    const response = await axios.post(
      endpoint,
      JSON.stringify({ admission_id: parseInt(admissionId, 10) }),
      {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }
    );

    return response.data;
  }

  // ─── API 5.6 — Get edited (saved) discharge draft HTML ──────────────
  /**
   * Fetch the saved/edited discharge-summary draft HTML for an admission.
   *
   * POST /api/get_edited_discharge_summary/
   *
   * The backend returns the raw HTML string of the doctor-edited draft (not
   * wrapped in JSON). Returned as-is so the caller can normalize and handle a
   * possible no-draft response ({} / { is_valid:false } / empty string).
   *
   * @param {number|string} admissionId - Admission ID.
   * @param {object} headers - Full auth headers.
   * @returns {Promise<string|object>} Raw HTML string (or no-draft payload).
   */
  async getEditedDischargeSummary(admissionId, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/get_edited_discharge_summary/`;

    const response = await axios.post(
      endpoint,
      JSON.stringify({ admission_id: parseInt(admissionId, 10) }),
      {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }
    );

    return response.data;
  }

  // ─── API 5.7 — Get finalized discharge PDF (presigned S3 URL) ────────
  /**
   * Fetch a short-lived presigned URL for the FINALIZED discharge-summary PDF
   * (the document produced by "Authenticate & Finalize", uploaded to S3).
   *
   * POST /api/get_discharge_pdf_url/
   *
   * @returns {Promise<object>} { status, found, url } — url is null when no
   *   finalized summary exists for the admission (found: false).
   */
  async getDischargePdfUrl(admissionId, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/get_discharge_pdf_url/`;

    const response = await axios.post(
      endpoint,
      JSON.stringify({ admission_id: parseInt(admissionId, 10) }),
      {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }
    );

    return response.data;
  }

  // ─── API 6 — Upload final discharge PDF to S3 ───────────────────────
  /**
   * Upload the final discharge PDF to S3.
   *
   * POST /api/upload_discharge_pdf/
   */
  async uploadDischargePdf(admissionId, pdfBlob, filename, headers) {
    const endpoint = `${MANAGE_DEV_BASE}/upload_discharge_pdf/`;

    const formData = new FormData();
    formData.append("admission_id", String(admissionId));
    
    // Convert Blob to File to ensure correct filename and mimetype for backend
    const pdfFile = new File([pdfBlob], filename || `discharge_summary_${admissionId}.pdf`, {
      type: "application/pdf"
    });
    
    formData.append("file", pdfFile);

    const response = await axios.post(endpoint, formData, {
      headers: {
        ...headers,
        // Let axios set Content-Type with boundary for FormData
        "Content-Type": undefined,
      },
    });

    return response.data;
  }
}

const dischargeSummaryServiceInstance = new DischargeSummaryService();
export default dischargeSummaryServiceInstance;
